import { ref, computed, onBeforeUnmount, watch } from 'vue'
import axios from 'axios'
import { useHeaderStore } from '@/stores/header'
import { useLoginStore } from '@/stores/login'
import { usePageVisibilityStore } from '@/stores/pageVisibility'
import { usePollingsStore, POLLING_LABELS } from '@/stores/pollings'
import { storeToRefs } from 'pinia'
import { exhaustiveCheck } from "@lecre/type-util"

export type TalkUnreadResultType = {
  isExistUnread: boolean,
  nextRequestParameter: string,
  waitTime: number,
}

type TalkUnreadArgs =
| { accountId: string }
| { parameter: string }

export function useTalkUnread() {
  const loginStore = useLoginStore()
  const pollingsStore = usePollingsStore()
  const { talkNotificationUrls } = storeToRefs(useHeaderStore())
  const { pageVisible } = storeToRefs(usePageVisibilityStore())

  const talkUnreadResult = ref<TalkUnreadResultType>()

  const accountInfo = computed(() => {
    return loginStore.currentAccount
  })

  // トークの未読をチェックする
  const checkTalkUnread = async (args?: TalkUnreadArgs): Promise<TalkUnreadResultType> => {
    // generateArgs を指定しているので args は常に存在する
    if (!args) throw new Error('args is required')

    if ('accountId' in args) {
      // 初回
      const resp = await axios<TalkUnreadResultType>({
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        url: talkNotificationUrls.value?.first,
        data: { accountId: args.accountId },
      })
      talkUnreadResult.value = resp.data
      return resp.data
    }
    if ('parameter' in args) {
      // 2回目以降
      const resp = await axios<TalkUnreadResultType>({
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        url: talkNotificationUrls.value?.next,
        data: { parameter: args.parameter },
      })
      talkUnreadResult.value = resp.data
      return resp.data
    }
    exhaustiveCheck(args)
  }

  // ポーリング開始
  const startPolling = () => {
    pollingsStore.start<TalkUnreadResultType, TalkUnreadArgs>(
      POLLING_LABELS.TALK_UNREAD_POLLING,
      checkTalkUnread,
      {
        generateArgs: (response) => {
          if (!response) {
            // 初回
            return { accountId: accountInfo.value.account_id }
          }
          // 2回目以降
          return { parameter: response.nextRequestParameter }
        },
        intervalMillis: (response) => {
          return (response.waitTime || 10) * 1000 // default 10 sec
        },
        continueOnError: true,
      }
    )
  }

  // ポーリング停止
  const stopPolling = () => {
    pollingsStore.stop(POLLING_LABELS.TALK_UNREAD_POLLING)
  }

  // pageVisible の変化を監視
  watch(pageVisible, (isVisible) => {
    if (isVisible) {
      // ページが見えるようになったら再開
      if (!pollingsStore.isPolling(POLLING_LABELS.TALK_UNREAD_POLLING)) {
        startPolling()
      }
      return
    }
    // ページが見えなくなったら停止
    stopPolling()
  }, { immediate: true })

  onBeforeUnmount(() => {
    stopPolling()
  })

  return {
    talkUnreadResult,
  }
}
